import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

interface UpdateTaxValuesParams {
    establishment_id: number | string | null;
    data_inicio: string;
    data_fim: string;
    pis_value: number;
    cofins_value: number;
}

interface UpdateTaxValuesResponse {
    success: boolean;
    message: string;
    data?: any;
}

export const useDrfTaxUpdate = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const updateTaxValues = useCallback(async (params: UpdateTaxValuesParams): Promise<UpdateTaxValuesResponse> => {
        // Validação básica dos parâmetros
        if (!params.establishment_id) {
            setError('Estabelecimento não selecionado');
            enqueueSnackbar('Selecione um estabelecimento', { variant: 'error' });
            return { success: false, message: 'Estabelecimento não selecionado' };
        }

        if (!params.data_inicio || !params.data_fim) {
            setError('Período não selecionado');
            enqueueSnackbar('Selecione um período', { variant: 'error' });
            return { success: false, message: 'Período não selecionado' };
        }

        if (params.pis_value < 0 || params.cofins_value < 0) {
            setError('Valores de PIS e COFINS não podem ser negativos');
            enqueueSnackbar('Valores de PIS e COFINS não podem ser negativos', { variant: 'error' });
            return { success: false, message: 'Valores negativos não permitidos' };
        }

        try {
            setLoading(true);
            setError(null);

            const token = document.cookie
                .split('; ')
                .find((row) => row.startsWith('authTokenMHP='))
                ?.split('=')[1];

            const response = await fetch(`${process.env.REACT_APP_API_URL}drf/update-tax-values`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    establishment_id: params.establishment_id,
                    data_inicio: params.data_inicio,
                    data_fim: params.data_fim,
                    pis_value: params.pis_value,
                    cofins_value: params.cofins_value
                })
            });

            const data = await response.json();

            if (response.ok && data.success !== false) {
                enqueueSnackbar('Valores de PIS e COFINS atualizados com sucesso', { variant: 'success' });
                return { 
                    success: true, 
                    message: 'Valores atualizados com sucesso', 
                    data: data.data 
                };
            } else {
                const errorMessage = data.message || 'Erro ao atualizar valores de tributos';
                setError(errorMessage);
                enqueueSnackbar(errorMessage, { variant: 'error' });
                return { success: false, message: errorMessage };
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
            setError(errorMessage);
            enqueueSnackbar(errorMessage, { variant: 'error' });
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    return {
        updateTaxValues,
        loading,
        error
    };
};
